import * as React from "react";
import { FC, useCallback, useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";
import { useParams } from "react-router";
import { bindActionCreators } from "redux";
import { InlineLoading } from "src/components/common";
import Tree, { ITree } from "src/components/common/Tree/Tree";
import { InstitutionNotesBreadcrumbs } from "src/helpers/breadcrumbHelper";
import useNavigate from "src/hooks/useNavigate";
import * as InstitutionActions from "../../../actions/institution";
import * as NavigationActions from "../../../actions/navigation";
import * as NoteTypesNotesActions from "../../../actions/noteTypeNotes";
import { RestrictedLayout } from "../../../components/layouts/index";
import { Link } from "../../../components/routing/index";
import {
    FontAwesomeIcon,
    FontAwesomeIcons,
} from "../../../constants/fontAwesomeIcons";
import * as Dtos from "../../../dtos/Tmd.dtos";
import { RequestState } from "../../../enumerations/RequestState";
import * as AuthenticationHelper from "../../../helpers/authentication";
import { InstitutionSubMenu } from "../../../helpers/subMenuHelper";
import { ITmdState } from "../../../reducers/index";

import "./InstitutionNotesPage.scss";

interface InstitutionNotePageProps {
    institution: Dtos.Institution;
    loadingInstitution: boolean;
    loadInstitutionSuccess: boolean;
    loadInstitutionFailure: boolean;
    loadInstitutionErrorCode: string;
    noteTypeNotes: Dtos.NoteTypeNotes[];
    loadingNoteTypeNotes: boolean;
    loadNoteTypeNotesSuccess: boolean;
    loadNoteTypeNotesFailure: boolean;
    loadNoteTypeNotesErrorCode: string;
    permissions?: Dtos.PersonnelTmdPermissions[];
    clearInstitution: InstitutionActions.IInstitutionClearActionCreator;
    loadInstitution: InstitutionActions.IInstitutionLoadByIdActionCreator;
    clearNoteTypeNotes: NoteTypesNotesActions.INoteTypeNotesClearActionCreator;
    loadNoteTypeNotes: NoteTypesNotesActions.INoteTypeNotesLoadByInstitutionIdActionCreator;
    setBreadcrumbs: NavigationActions.ISetBreadcrumbsActionCreator;
    setDrawerItems: NavigationActions.ISetDrawerItemsActionCreator;
}

const InstitutionNotePage: FC<InstitutionNotePageProps> = ({
    institution,
    loadingInstitution,
    loadInstitutionSuccess,
    loadInstitutionFailure,
    loadInstitutionErrorCode,
    noteTypeNotes,
    loadingNoteTypeNotes,
    loadNoteTypeNotesSuccess,
    loadNoteTypeNotesFailure,
    loadNoteTypeNotesErrorCode,
    permissions,
    clearInstitution,
    loadInstitution,
    clearNoteTypeNotes,
    loadNoteTypeNotes,
    setBreadcrumbs,
    setDrawerItems
}) => {
    //
    // -- Params --
    const { institutionId } = useParams<{ institutionId: string }>();
    const institutionIdNumber = parseInt(institutionId, 10);

    //
    // -- States --
    const [treeData, setTreeData] = useState<ITree | null>(null);
    const [initalizedTreeData, setInitalizedTreeData] =
        useState<boolean>(false);
    const [toggleState, setToggleState] = useState<string[]>([]);
    const [creatingTree, setCreatingTree] = useState<boolean>(false);

    //
    // -- Hooks --
    const navigate = useNavigate();

    //
    // -- Variables --
    const showInstitution =
        !loadingInstitution && 
        loadInstitutionSuccess && 
        !loadingNoteTypeNotes && 
        loadNoteTypeNotesSuccess && 
        !creatingTree;


    // Load institution
    useEffect(() => {
        if (institutionIdNumber && !institution) {
            setDrawerItems(
                InstitutionSubMenu(institutionIdNumber, permissions)
            );
            setBreadcrumbs(InstitutionNotesBreadcrumbs(institutionIdNumber));

            setTreeData(null);

            loadInstitution(institutionIdNumber);
            
            loadNoteTypeNotes(institutionIdNumber);
        }
    }, [
        institutionIdNumber,
        institution,
        permissions,
        setDrawerItems,
        setBreadcrumbs,
        loadInstitution,
        setTreeData,
        InstitutionSubMenu,
        InstitutionNotesBreadcrumbs,
        loadNoteTypeNotes
    ]);

    // Navigate to error page on load failure
    useEffect(() => {
        if (loadInstitutionFailure) {
            navigate(`/error/${loadInstitutionErrorCode}`);
        }
        if (loadNoteTypeNotesFailure) {
            navigate(`/error/${loadNoteTypeNotesErrorCode}`);
        }
    }, [
        loadInstitutionFailure, 
        loadInstitutionErrorCode, 
        loadNoteTypeNotesFailure, 
        loadNoteTypeNotesErrorCode, 
        navigate
    ]);

    // Update breadcrumbs
    useEffect(() => {
        if (institutionIdNumber && institution?.name) {
            setBreadcrumbs(
                InstitutionNotesBreadcrumbs(
                    institutionIdNumber,
                    institution.name
                )
            );
        }
    }, [
        institution,
        institutionIdNumber,
        permissions,
        setDrawerItems,
        setBreadcrumbs,
        InstitutionNotesBreadcrumbs,
    ]);

    // Cleanup effect to clear the institution on component unmount
    useEffect(() => {
        return () => {
            setTreeData(null);
            clearInstitution();
            clearNoteTypeNotes();
        };
    }, [clearInstitution, clearNoteTypeNotes, setTreeData]);

    // Update tree
    useEffect(() => {
        setInitalizedTreeData(false);
    }, [noteTypeNotes, setInitalizedTreeData]);

    // Create tree
    useEffect(() => {
        if (noteTypeNotes && !initalizedTreeData) {
            setCreatingTree(true);

            // If no institution notes return
            if (noteTypeNotes?.length == 0) {
                setCreatingTree(false);
                return;
            }

            const createChildNodes = (notes) => {
                let children = [];

                children =
                    notes
                        ?.sort((a, b) => a?.id - b?.id)
                        ?.map((y) => ({
                            id: `note-${y.id}`,
                            data: (
                                <div
                                    style={{
                                        display: "grid",
                                        gridTemplateColumns: " 1fr 3fr 8fr",
                                    }}
                                >
                                    <div className="px-1">
                                        <FontAwesomeIcon
                                            icon={FontAwesomeIcons.Duotone.NOTE}
                                        />
                                    </div>
                                    <div className="px-1">{y?.title}</div>
                                    <div className="d-inline-block px-1">
                                        <span
                                            className="text-truncate"
                                            style={{ whiteSpace: "pre-line" }}
                                        >
                                            {y?.comments}
                                        </span>
                                    </div>
                                </div>
                            ),
                            icon: null,
                            collapsible: false,
                        })) ?? [];

                // Add table header
                if (children?.length > 0) {
                    children.unshift({
                        id: "tableheader",
                        data: (
                            <div
                                style={{
                                    display: "grid",
                                    gridTemplateColumns: "1fr 3fr 8fr",
                                }}
                            >
                                <div className="px-1"></div>
                                <div className="px-1">
                                    <strong>Title</strong>
                                </div>
                                <div className="px-1">
                                    <strong>Comment</strong>
                                </div>
                            </div>
                        ),
                        icon: null,
                        collapsible: false,
                    });
                }

                return children;
            };

            let treeNodes =
                noteTypeNotes
                    ?.sort((a, b) =>
                        a?.noteType?.value.localeCompare(b?.noteType?.value)
                    )
                    ?.map((x) => ({
                        id: `type-${x.noteTypeId}`,
                        data: (
                            <div className="d-flex align-items-center">
                                <FontAwesomeIcon
                                    icon={FontAwesomeIcons.Duotone.NOTESMEDICAL}
                                />
                                <span
                                    className="pl-4 mb-0"
                                    style={{ width: "180px" }}
                                >
                                    {x.noteType?.value}
                                </span>
                                <div
                                    className="d-flex align-items-center justify-content-center"
                                    style={{ width: "50px" }}
                                >
                                    <span className="badge badge-pill badge-secondary mb-0">
                                        {x?.notes?.length}
                                    </span>
                                </div>
                            </div>
                        ),
                        icon: null,
                        collapsible: true,
                        subTree: {
                            children: [...createChildNodes(x.notes)],
                        },
                    })) ?? [];

            // Initialize tree toggle
            const expanded = treeNodes.map((z) => z.id);

            setToggleState(expanded);
            setTreeData({ children: treeNodes });
            setInitalizedTreeData(true);
            setCreatingTree(false);
        }
    }, [
        noteTypeNotes,
        initalizedTreeData,
        treeData,
        setTreeData,
        setToggleState,
        setInitalizedTreeData,
        setCreatingTree,
    ]);

    const handleToggle = useCallback(
        (id: string, navigateFlag?: boolean) => {
            if (navigateFlag) {
                if (
                    id.includes("note") &&
                    AuthenticationHelper.hasPermission(
                        permissions,
                        Dtos.Permission.InstitutionNoteView
                    )
                ) {
                    navigate(
                        `/institutions/${institutionIdNumber}/notes/${id.replace(
                            "note-",
                            ""
                        )}`
                    );
                }
                if (id.includes("type")) {
                    setToggleState((prev) =>
                        prev.includes(id)
                            ? prev.filter((nodeId) => nodeId !== id)
                            : [...prev, id]
                    );
                }
            } else {
                setToggleState((prev) =>
                    prev.includes(id)
                        ? prev.filter((nodeId) => nodeId !== id)
                        : [...prev, id]
                );
            }
        },
        [
            setToggleState,
            navigate,
            institutionIdNumber,
            permissions,
            institution,
        ]
    );

    const renderContent = useMemo(() => {
        return (
            <div>
                <div className="row">
                    <div className={"col-9 mb-2"}>
                        <h1>Institution Notes</h1>
                    </div>
                    <div className="col-3" style={{ textAlign: "right" }}>
                        {AuthenticationHelper.hasPermission(
                            permissions,
                            Dtos.Permission.InstitutionNoteCreate,
                            null,
                            institutionIdNumber
                        ) &&
                            institution &&
                            institution.id > 0 && (
                                <Link
                                    className={"btn btn-primary"}
                                    url={`/institutions/${institution.id}/notes/create`}
                                >
                                    Create
                                </Link>
                            )}
                    </div>
                </div>
                {!showInstitution ? (
                    <InlineLoading absoluteCenter={true} size={50} />
                ) : null}
                {showInstitution ? (
                    <>
                        {treeData && treeData?.children?.length > 0 ? (
                            <Tree
                                treeData={treeData}
                                onToggle={handleToggle}
                                toggleState={toggleState}
                            />
                        ) : (
                            <div className="card card-shadow p-3">
                                <div className="alert alert-info d-flex mb-0">
                                    <div className="mr-2">
                                        <FontAwesomeIcon
                                            icon={
                                                FontAwesomeIcons.Solid
                                                    .INFO_SQUARE
                                            }
                                            fixedWidth
                                        />
                                    </div>
                                    <span>
                                        There are currently no institution notes
                                    </span>
                                </div>
                            </div>
                        )}
                    </>
                ) : null}
            </div>
        );
    }, [
        permissions,
        institutionIdNumber,
        institution,
        showInstitution,
        treeData,
        handleToggle,
        toggleState,
    ]);

    return (
        <RestrictedLayout
            permission={Dtos.Permission.InstitutionNoteView}
            loading={loadingInstitution || loadingNoteTypeNotes}
        >
            {renderContent}
        </RestrictedLayout>
    );
};

const mapStateToProps = (
    state: ITmdState
): Partial<InstitutionNotePageProps> => {
    let institution: Dtos.Institution | undefined = !(
        state.institutions.formData instanceof Array
    )
        ? state.institutions.formData
        : undefined;
    let noteTypeNotes: Dtos.NoteTypeNotes[] | undefined = (state.noteTypeNotes.formData instanceof Array)
            ? state.noteTypeNotes.formData
            : undefined;
    let permissions = state.user.userSummary
        ? state.user.userSummary.permissions
        : [];

    return {
        institution: institution!,
        loadingInstitution:
            state.institutions.loadState &&
            state.institutions.loadState.status == RequestState.Pending,
        loadInstitutionSuccess:
            state.institutions.loadState &&
            state.institutions.loadState.status == RequestState.Success,
        loadInstitutionFailure:
            state.institutions.loadState &&
            state.institutions.loadState.status == RequestState.Failure,
        loadInstitutionErrorCode: state.institutions.loadState.errorCode,
        noteTypeNotes: noteTypeNotes!,
        loadingNoteTypeNotes:
            state.noteTypeNotes.loadState &&
            state.noteTypeNotes.loadState.status == RequestState.Pending,
        loadNoteTypeNotesSuccess:
            state.noteTypeNotes.loadState &&
            state.noteTypeNotes.loadState.status == RequestState.Success,
        loadNoteTypeNotesFailure:
            state.noteTypeNotes.loadState &&
            state.noteTypeNotes.loadState.status == RequestState.Failure,
        loadNoteTypeNotesErrorCode: state.noteTypeNotes.loadState.errorCode,
        permissions: permissions,
    };
};

const mapDispatchToProps = (dispatch): Partial<InstitutionNotePageProps> => {
    return {
        loadInstitution: bindActionCreators(InstitutionActions.LoadInstitutionById, dispatch),
        clearInstitution: bindActionCreators(InstitutionActions.Clear, dispatch),
        loadNoteTypeNotes: bindActionCreators(NoteTypesNotesActions.LoadNoteTypeNotesByInstitutionId, dispatch),
        clearNoteTypeNotes: bindActionCreators(NoteTypesNotesActions.Clear, dispatch),
        setBreadcrumbs: bindActionCreators(NavigationActions.SetBreadcrumbs, dispatch),
        setDrawerItems: bindActionCreators(NavigationActions.SetDrawerItems, dispatch)
    };
};

export default connect(
    mapStateToProps,
    mapDispatchToProps
)(InstitutionNotePage);
